import React from "react";
import { MapPin } from "lucide-react";
import {
  formatVenueSecondaryLine,
  groupInstancesByVenue,
  shouldShowVenueGroups,
} from "../lib/classSessionHelpers";

/**
 * Renders class instance rows, split by venue when more than one location
 * appears in the list. `renderRow(inst)` returns an `<li>`.
 */
export default function ClassesByVenueList({
  instances,
  venues = [],
  renderRow,
  listClassName = "pt-2 divide-y divide-black/[0.08]",
  groupedListClassName = "divide-y divide-black/[0.08]",
}) {
  const groups = groupInstancesByVenue(instances, venues);
  if (!groups.length) return null;

  if (!shouldShowVenueGroups(groups)) {
    return (
      <ul className={listClassName}>
        {groups[0].instances.map((inst) => renderRow(inst))}
      </ul>
    );
  }

  return (
    <div className="pt-2 space-y-5" data-testid="classes-by-venue">
      {groups.map((group) => {
        const secondary = formatVenueSecondaryLine(group.address);
        return (
          <section
            key={group.key}
            data-testid={`classes-venue-group-${group.key}`}
            aria-labelledby={`venue-heading-${group.key}`}
          >
            <header className="pb-1.5">
              <h3
                id={`venue-heading-${group.key}`}
                className="font-ui font-medium text-[13px] text-ink/70 flex items-start gap-1.5"
              >
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.25} aria-hidden />
                <span className="min-w-0 leading-snug text-pretty">{group.label}</span>
              </h3>
              {secondary?.kind === "map" ? (
                <p className="mt-0.5 pl-5">
                  <a
                    href={secondary.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${secondary.label} (opens in a new tab)`}
                    className="font-ui font-medium text-[13px] text-plum hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {secondary.label}
                  </a>
                </p>
              ) : secondary ? (
                <p className="mt-0.5 pl-5 font-ui font-medium text-[13px] text-ink/60 leading-snug text-pretty">
                  {secondary.label}
                </p>
              ) : null}
            </header>
            <ul className={groupedListClassName}>
              {group.instances.map((inst) => renderRow(inst))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
